// A room's feature, or a drawn wall's, moved by the edit tools in a section view:
// along its wall — a pane, an opening, a door on an outer wall — or its bench — a
// bench support —, and up or down — a pane, an opening, a partition, a heater
// wall, a bench's seat. A door stands on the floor and a support under its bench,
// so they only slide. Every move is to an absolute place (where it
// started plus the move so far), so a clamp never drifts. Pure.

import type {
  GlassSegment,
  ProductInstance,
  Vec2,
  Wall,
  WallFeature,
} from '../shared/types';
import type { SecPart } from './section';
import {
  DOOR_SIDE_MIN,
  dragDoorAlong,
  polyEdges,
  segToWall,
  wallChain,
  writeWallChain,
} from './polyRoom';
import type {
  VertexRemap,
  WallDimSpan,
} from './polyRoom';
import {
  glassPaneBase,
  glassPaneTop,
  heaterWallBase,
  innerWallBase,
  openingArchitrave,
  openingSill,
  wallBase,
  wallHeight,
} from './heights';
import {
  wallFeatureBase,
  wallFeatureTop,
  wallSegFrame,
} from './wallFeatures';
import { roomBenchSeats } from './ceiling';
import {
  benchSupportMarks,
  type TrestleMark,
} from './benchSolids';
import { placeSupport } from './benchSupports';
import { benchLegOf } from './roomBench';
import { roomDoors } from './door';
import { rotatePoint } from './geometry';
import { featureCeiling } from './handles';
import { roomDoor } from './roomDoor';

/** Why a feature does not rise: a partition that follows the ceiling has no
 *  height of its own; a steam bench swept from a profile takes its seat from it. */
export type FeatureStuck = 'follows-ceiling' | 'profile';

/** Where a feature stands, as the section tools move it: `along` its wall (mm,
 *  a station along the wall's direction `dir`, world; null: it does not slide) and
 *  `up` (its base, a bench its seat — mm; null: it does not rise, `stuck` says
 *  why when it is not a door). */
export interface FeatureSpot {
  along: number | null;
  dir: Vec2 | null;
  up: number | null;
  stuck?: FeatureStuck;
}

const paneOf = (inst: ProductInstance, part: SecPart): GlassSegment | undefined =>
  (part.kind === 'pane' ? inst.glass : part.kind === 'opening' ? inst.openings : undefined)?.find((g) => g.id === part.id);

/** A room edge's direction in the world. */
function edgeDir(inst: ProductInstance, edge: number): Vec2 | null {
  const e = polyEdges(inst)[edge];
  return e ? rotatePoint(e.dir, inst.rotation) : null;
}

/** Where a pane or an opening on an outer wall starts along its wall (its chain's
 *  station), and the chain. */
function chainSpot(inst: ProductInstance, seg: GlassSegment): { wall: number; spans: WallDimSpan[]; i: number } | null {
  if (seg.innerWallId != null || seg.edge == null) return null;
  const sw = segToWall(inst, seg.edge);
  if (!sw) return null;
  const spans = wallChain(inst, sw.wall);
  const i = spans.findIndex((s) => s.feature === seg);
  return i < 0 ? null : { wall: sw.wall, spans, i };
}

/** The trestle a support's place reads: the one under bench `part.bench` when
 *  it stands there, else its first. */
function supportMarkOf(inst: ProductInstance, part: SecPart): TrestleMark | null {
  const marks = benchSupportMarks(inst).filter((m) => m.support === part.id && m.sr);
  return marks.find((m) => m.bench === part.bench) ?? marks[0] ?? null;
}

/** A support's run's direction (world) at its trestle: a room's runs are in its
 *  frame, a standalone bench's in the world. */
function supportDir(inst: ProductInstance, m: TrestleMark): Vec2 | null {
  const a = m.point(m.at - 0.5), b = m.point(m.at + 0.5), l = Math.hypot(b.x - a.x, b.y - a.y);
  if (l < 1e-9) return null;
  const d = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
  return inst.category === 'saunabench' ? d : rotatePoint(d, inst.rotation);
}

/** A room's feature, where it stands; null for one not found or one that does
 *  not move here (a backrest moves with its bench). A support stands at its
 *  trestle's station along its bench's run. */
export function featureSpot(inst: ProductInstance, part: SecPart): FeatureSpot | null {
  switch (part.kind) {
    case 'pane':
    case 'opening': {
      const seg = paneOf(inst, part);
      if (!seg) return null;
      const c = chainSpot(inst, seg);
      const up = part.kind === 'pane' ? glassPaneBase(seg) : openingSill(seg);
      return { along: c ? c.spans[c.i].s : null, dir: c && seg.edge != null ? edgeDir(inst, seg.edge) : null, up };
    }
    case 'door': {
      const door = roomDoors(inst).find((d) => d.id === part.id);
      if (!door) return null;
      const rd = door.innerWallId == null ? roomDoor(inst, door) : null;
      return { along: rd ? (rd.span.start + rd.span.end) / 2 : null, dir: rd ? edgeDir(inst, door.edge ?? 0) : null, up: null };
    }
    case 'partition': {
      const iw = (inst.innerWalls ?? []).find((w) => w.id === part.id);
      if (!iw) return null;
      return iw.height == null ? { along: null, dir: null, up: null, stuck: 'follows-ceiling' } : { along: null, dir: null, up: innerWallBase(iw) };
    }
    case 'heaterWall': {
      const hw = (inst.heaterWalls ?? []).find((w) => w.id === part.id);
      return hw ? { along: null, dir: null, up: heaterWallBase(hw) } : null;
    }
    case 'bench': {
      const leg = benchLegOf(inst, part.id), seat = roomBenchSeats(inst).find((s) => s.id === part.id);
      if (!leg || !seat) return null;
      return leg.profile ? { along: null, dir: null, up: null, stuck: 'profile' } : { along: null, dir: null, up: leg.height ?? seat.seat };
    }
    case 'support': {
      const m = supportMarkOf(inst, part);
      return m ? { along: m.at, dir: supportDir(inst, m), up: null } : null;
    }
    case 'fixture':
    case 'wallFeature':
      return null;
  }
}

/** Slide a pane or an opening `d` mm along its wall: the solid run it moves into
 *  shrinks (to nothing, or to a door's side minimum when a door is beyond it), the
 *  one behind it grows — or opens, at a corner or another feature. It does not
 *  pass a feature, nor change a pinned run. */
function slideOnWall(inst: ProductInstance, seg: GlassSegment, d: number): VertexRemap | null {
  const c = chainSpot(inst, seg);
  if (!c || Math.abs(d) < 1e-9) return null;
  const { spans, i } = c;
  const fwd = d > 0;
  const toward = spans[fwd ? i + 1 : i - 1], away = spans[fwd ? i - 1 : i + 1];
  if (!toward || toward.kind !== 'solid' || toward.locked || (away?.kind === 'solid' && away.locked)) return null;
  const beyond = spans[fwd ? i + 2 : i - 2];
  const room = Math.max(0, toward.t - toward.s - (beyond?.kind === 'door' ? DOOR_SIDE_MIN : 0));
  const m = Math.min(Math.abs(d), room);
  if (m < 1e-9) return null;
  const out = spans.slice(), lens = spans.map((s) => s.t - s.s);
  lens[fwd ? i + 1 : i - 1] -= m;
  if (away?.kind === 'solid') lens[fwd ? i - 1 : i + 1] += m;
  else {
    const at = fwd ? i : i + 1;
    out.splice(at, 0, { kind: 'solid', s: 0, t: 0, locked: false });
    lens.splice(at, 0, m);
  }
  return writeWallChain(inst, c.wall, out, lens);
}

/** Move a room's feature to `to` (either part left out stays): a slide along its
 *  wall (a support along its bench, to whole mm, flush when it reaches an end)
 *  and a rise keeping its own height (a pane's top, an opening's architrave,
 *  a partition's and a heater wall's height above its base go with it), never
 *  below the floor — and a pane or an opening never up into the ceiling
 *  (`featureCeiling`: it stops where its top meets it). Returns the vertex remap
 *  of a wall re-laid, else null. */
export function placeFeature(inst: ProductInstance, part: SecPart, to: { along?: number; up?: number }): VertexRemap | null {
  const now = featureSpot(inst, part);
  if (!now) return null;
  let remap: VertexRemap | null = null;
  if (to.along != null && now.along != null) {
    if (part.kind === 'door') {
      const door = roomDoors(inst).find((x) => x.id === part.id);
      if (door) dragDoorAlong(inst, door, to.along);
    } else if (part.kind === 'support') {
      const m = supportMarkOf(inst, part), sup = inst.benchSupports?.find((x) => x.id === part.id);
      if (m?.sr && sup) placeSupport(sup, m.sr, Math.round(to.along), 0.5);
    } else {
      const seg = paneOf(inst, part);
      if (seg) remap = slideOnWall(inst, seg, to.along - now.along);
    }
  }
  if (to.up == null || now.up == null) return remap;
  let rise = Math.max(0, to.up) - now.up;
  const g0 = rise > 0 ? paneOf(inst, part) : undefined, cap = g0 ? featureCeiling(inst, part) : null;
  if (g0 && cap != null) rise = Math.min(rise, Math.max(0, cap - (part.kind === 'pane' ? glassPaneTop(inst, g0) : openingArchitrave(inst, g0))));
  if (Math.abs(rise) < 1e-9) return remap;
  const up = now.up + rise;
  switch (part.kind) {
    case 'pane': {
      const g = paneOf(inst, part);
      if (!g) break;
      g.height = glassPaneTop(inst, g) + rise;
      if (up > 0) g.heightFromGround = up; else delete g.heightFromGround;
      break;
    }
    case 'opening': {
      const o = paneOf(inst, part);
      if (!o) break;
      o.architraveHeight = openingArchitrave(inst, o) + rise;
      o.heightFromGround = up;
      break;
    }
    case 'partition': {
      const iw = (inst.innerWalls ?? []).find((w) => w.id === part.id);
      if (iw) iw.heightFromGround = up;
      break;
    }
    case 'heaterWall': {
      const hw = (inst.heaterWalls ?? []).find((w) => w.id === part.id);
      if (hw) hw.heightFromGround = up;
      break;
    }
    case 'bench': {
      const leg = benchLegOf(inst, part.id);
      if (leg) leg.height = Math.round(up);
      break;
    }
    case 'door':
    case 'fixture':
    case 'support':
    case 'wallFeature':
      break;
  }
  return remap;
}

/** A drawn wall's door, pane or opening, where it stands: its start along its
 *  segment, and its base (a door none: it stands on the floor). */
export function wallFeatureSpot(w: Wall, f: WallFeature): FeatureSpot | null {
  const fr = wallSegFrame(w, f.seg);
  if (!fr) return null;
  return { along: Math.min(f.start, f.end), dir: fr.u, up: f.kind === 'door' ? null : wallFeatureBase(w, f) };
}

/** Move a drawn wall's feature to `to`: along its segment, stopping at its
 *  straight run's ends and at its neighbours; up keeping its own height, its top
 *  stopping at the wall's. */
export function placeWallFeature(w: Wall, f: WallFeature, to: { along?: number; up?: number }): void {
  const fr = wallSegFrame(w, f.seg);
  if (!fr) return;
  const s = Math.min(f.start, f.end), e = Math.max(f.start, f.end), width = e - s;
  if (to.along != null) {
    let lo = fr.lo, hi = fr.hi;
    for (const o of w.features ?? []) {
      if (o === f || o.seg !== f.seg) continue;
      if (Math.max(o.start, o.end) <= s + 1e-6) lo = Math.max(lo, Math.max(o.start, o.end));
      else if (Math.min(o.start, o.end) >= e - 1e-6) hi = Math.min(hi, Math.min(o.start, o.end));
    }
    const st = Math.max(lo, Math.min(hi - width, to.along));
    f.start = st; f.end = st + width;
  }
  if (to.up != null && f.kind !== 'door') {
    const b0 = wallFeatureBase(w, f), top = wallFeatureTop(w, f);
    let rise = Math.max(0, to.up) - b0;
    if (rise > 0) rise = Math.min(rise, Math.max(0, wallBase(w) + wallHeight(w) - top));
    if (Math.abs(rise) < 1e-9) return;
    f.height = top + rise;
    f.heightFromGround = b0 + rise;
  }
}
